import CustomLoader from "@/components/CustomLoader/customLoader.component";
import { fetchTagMetrics } from "@/models/tags.model";
import { Tag, TagMetrics } from "@/types";
import React, { useEffect, useRef, useState } from "react";

type TagsViewSectionMetricsProps = {
  viewingTag: Tag;
};

const TagsViewSectionMetrics: React.FC<TagsViewSectionMetricsProps> = ({
  viewingTag,
}) => {
  const [tagMetrics, setTagMetrics] = useState<TagMetrics | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const controllerRef = useRef(new AbortController());

  useEffect(() => {
    const controller = new AbortController();
    controllerRef.current = controller;
    const signal = controller.signal;
    async function fetchData() {
      setTagMetrics(null);
      setLoading(true);
      setError(null);
      const response = await fetchTagMetrics({
        tagID: viewingTag.id,
        signal: signal,
      });

      if (signal.aborted) {
        return;
      }
      setLoading(false);
      if (response.rStatus === "error") {
        setError(response.message);

        return;
      }

      setTagMetrics(response.metrics);
    }

    fetchData();

    return () => {
      controllerRef.current.abort("Cleanup - TagsViewSectionMetrics unmounted");
    };
  }, [viewingTag.id]);

  return (
    <div>
      <h1 className="text-lg font-bold text-center">Metrics</h1>
      {loading ? (
        <CustomLoader />
      ) : error ? (
        <div className="text-red-500 text-center">{error}</div>
      ) : !tagMetrics ? (
        <div className="text-center">
          <div>No metrics available</div>
        </div>
      ) : (
        <div className="flex flex-col gap-0 mt-8">
          <div className="flex justify-between items-center hover:bg-muted p-1 px-2 rounded">
            <div className="font-bold">Total Tasks Tagged:</div>
            <div>{tagMetrics.current_tasks_count}</div>
          </div>
          <div className="flex justify-between items-center hover:bg-muted p-1 px-2 rounded">
            <div className="font-bold">Tasks Created in Last 7 days:</div>
            <div>{tagMetrics.created_tasks_count["7_days"]}</div>
          </div>
          <div className="flex justify-between items-center hover:bg-muted p-1 px-2 rounded">
            <div className="font-bold">Tasks Created in Last 30 days:</div>
            <div>{tagMetrics.created_tasks_count["30_days"]}</div>
          </div>
          <div className="flex justify-between items-center hover:bg-muted p-1 px-2 rounded">
            <div className="font-bold">Tasks Created in Last 90 days:</div>
            <div>{tagMetrics.created_tasks_count["90_days"]}</div>
          </div>
          <div className="flex justify-between items-center hover:bg-muted p-1 px-2 rounded">
            <div className="font-bold">Tasks Completed:</div>
            <div>{tagMetrics.completed_tasks_count}</div>
          </div>
          {/* Active vs Inactive Tasks How many tasks with this tag are still active vs resolved. */}
          <div className="flex justify-between items-center hover:bg-muted p-1 px-2 rounded">
            <div className="font-bold">Active / Resolved Tasks:</div>
            <div>
              {tagMetrics.current_tasks_count -
                tagMetrics.completed_tasks_count}{" "}
              / {tagMetrics.completed_tasks_count}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TagsViewSectionMetrics;
